import { useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { SpinningLoader } from "risa-oasis-ui_v2";
import { SortIcon } from "../../svg/sortIcon";
import { sortRows } from "./sorting";
import type { CustomTableProps, SortDirection } from "./types";
import "./custom-table.scss";

/**
 * Generic, config-driven table following the shared RISA custom-table pattern
 * (column definitions + typed/custom cell renderers + sortable headers), but
 * self-contained: it holds its own sort state and uses the oasis
 * `SpinningLoader` for the loading state instead of the redux-backed table
 * context the other dashboards use.
 *
 * @typeParam T - The row data type.
 * @param props - {@link CustomTableProps}
 * @returns The rendered table (or loading/empty state)
 */
export function CustomTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  rowClassName,
  isLoading,
  emptyMessage = "No records found.",
  primaryComparator,
  initialSortKey = null as unknown as string,
  initialSortDirection = "asc",
  sortKey: controlledSortKey,
  sortDirection: controlledSortDirection,
  onSortChange,
  rowAction,
}: CustomTableProps<T>) {
  // Controlled when the parent wires up `onSortChange`; otherwise the table
  // owns its sort locally (backward-compatible with existing consumers).
  const isControlled = onSortChange != null;
  const [localSortKey, setLocalSortKey] = useState<string | null>(
    initialSortKey ?? null,
  );
  const [localSortDirection, setLocalSortDirection] =
    useState<SortDirection>(initialSortDirection);

  const sortKey = isControlled ? (controlledSortKey ?? null) : localSortKey;
  const sortDirection = isControlled
    ? (controlledSortDirection ?? "asc")
    : localSortDirection;

  const handleSort = (key: string) => {
    const nextDirection: SortDirection =
      sortKey === key && sortDirection === "asc" ? "desc" : "asc";
    if (isControlled) {
      onSortChange(key, nextDirection);
      return;
    }
    if (sortKey === key) {
      setLocalSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setLocalSortKey(key);
      setLocalSortDirection("asc");
    }
  };

  const sortedRows = useMemo(
    () => sortRows(rows, columns, sortKey, sortDirection, primaryComparator),
    [rows, columns, sortKey, sortDirection, primaryComparator],
  );

  // Windowed rendering: worklists can hold thousands of document rows, so only
  // the visible slice (plus overscan) is mounted. Rows have variable height
  // (wrapping diagnoses, tag chips), so heights are measured, not assumed.
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: sortedRows.length,
    getScrollElement: () => scrollRef.current,
    // Key the measurement cache by ROW, not index: rows reorder on sort and are replaced on
    // tab/date/filter change, so an index-keyed height would follow the slot instead of the row
    // and skew getTotalSize() (scroll jumps + a mis-sized scrollbar after sorting a long list).
    getItemKey: (index) => rowKey(sortedRows[index]),
    estimateSize: () => 56,
    // No paddingStart despite <thead> living inside the scroll container: rows render via in-flow
    // spacer <tr>s inside <tbody> (below), so the browser's table layout already offsets them past
    // the header. A paddingStart would inject the header height a second time and push rows down.
    overscan: 12,
  });
  const virtualRows = virtualizer.getVirtualItems();
  const totalColumns = columns.length + (rowAction ? 1 : 0);
  const paddingTop = virtualRows.length ? virtualRows[0].start : 0;
  const paddingBottom = virtualRows.length
    ? virtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
    : 0;

  if (isLoading) {
    return (
      <div className="ct-table__state" role="status" aria-live="polite">
        <SpinningLoader />
      </div>
    );
  }

  return (
    <div className="ct-table">
      {/* Inner wrapper owns the scroll so the card frame + accent bar stay
          pinned while the sticky header freezes and rows scroll. */}
      <div className="ct-table__scroll" ref={scrollRef}>
        <table className="ct-table__table">
          <thead>
            <tr className="ct-table__head-row">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`ct-table__th ct-table__th--${col.align ?? "left"} ${
                    col.sortable ? "ct-table__th--sortable" : ""
                  }`}
                  style={{ width: col.width }}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                >
                  <span className="ct-table__th-label">
                    {col.label}
                    {col.sortable && (
                      <SortIcon
                        active={sortKey === col.key}
                        direction={sortDirection}
                      />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paddingTop > 0 && (
              <tr aria-hidden>
                <td colSpan={totalColumns} style={{ height: paddingTop, padding: 0, border: 0 }} />
              </tr>
            )}
            {virtualRows.map((virtualRow) => {
              const row = sortedRows[virtualRow.index];
              return (
              <tr
                key={rowKey(row)}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                className={`ct-table__row ${
                  onRowClick ? "ct-table__row--clickable" : ""
                } ${rowClassName?.(row) ?? ""}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`ct-table__td ct-table__td--${col.align ?? "left"}`}
                  >
                    {col.render
                      ? col.render(row)
                      : (col.sortAccessor?.(row) ?? "")}
                  </td>
                ))}
                {rowAction && (
                  // Out-of-flow cell: absolutely positioned over the row's right edge, so it
                  // adds NO column width (no horizontal scroll) and floats at the VISIBLE right
                  // edge. Hidden until the row is hovered; click never bubbles to the row.
                  <td className="ct-table__row-action-cell">
                    <span
                      className="ct-table__row-action"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {rowAction(row)}
                    </span>
                  </td>
                )}
              </tr>
              );
            })}
            {paddingBottom > 0 && (
              <tr aria-hidden>
                <td colSpan={totalColumns} style={{ height: paddingBottom, padding: 0, border: 0 }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {sortedRows.length === 0 && (
        <div className="ct-table__state ct-table__state--empty">
          {emptyMessage}
        </div>
      )}
    </div>
  );
}

export default CustomTable;
